import { create } from 'zustand';
import { candidateType, relayServers, sanitizeSignal, type IceServerConfig, type SignalPayload, type VoiceState } from '@spawnwire/shared';
import { actions, useStore } from '../store/store';
import { ui } from '../store/ui';
import { http } from './api';
import { getSocket, onReady } from './gateway';
import { micLabel } from './local';
import { playChime } from './notifications';

/**
 * WebRTC calls as a full mesh: every participant holds one RTCPeerConnection
 * per other participant. Signaling (SDP and ICE candidates) is relayed by the
 * server over Socket.IO. Media always goes through the TURN relay, never
 * directly between participants, so nobody in a call learns anyone else's IP
 * address; when the relay cannot be reached the call fails instead of
 * connecting directly. Negotiation uses the "perfect negotiation" pattern so
 * either side can add or remove tracks at any time without offer collisions.
 */

type StreamKind = 'camera' | 'screen' | 'audio';

interface RemoteMedia {
  camera: MediaStream | null;
  screen: MediaStream | null;
  connection: RTCPeerConnectionState;
  /** Repeated attempts to reach this person through the relay have failed (still retrying). */
  relayFailed: boolean;
}

const RELAY_UNREACHABLE =
  "Couldn't reach the voice relay server. To keep your IP address private, calls only connect through it, so you can't join right now. Check your internet connection or firewall and try again.";
const RELAY_NOT_CONFIGURED = 'Calls are unavailable: this server has no voice relay set up, and calls never connect directly so IP addresses stay private.';
const RELAY_TIMEOUT_MS = 10_000;
/** Quick ICE restarts before a peer is shown as failing; after that it keeps retrying slowly. */
const QUICK_RETRIES = 3;
/** Reminders the polite side sends while it waits for the first offer, and the time between them (see awaitFirstOffer). */
const FIRST_OFFER_REMINDERS = 3;
const FIRST_OFFER_REMINDER_MS = 2_500;

export interface VoiceUiState {
  channelId: string | null;
  status: 'idle' | 'connecting' | 'connected' | 'error';
  error: string | null;
  muted: boolean;
  deafened: boolean;
  video: boolean;
  screen: boolean;
  hasMic: boolean;
  speaking: Record<string, boolean>;
  remote: Record<string, RemoteMedia>;
  localCamera: MediaStream | null;
  localScreen: MediaStream | null;
  userVolumes: Record<string, number>;
}

export const useVoice = create<VoiceUiState>(() => ({
  channelId: null,
  status: 'idle',
  error: null,
  muted: false,
  deafened: false,
  video: false,
  screen: false,
  hasMic: true,
  speaking: {},
  remote: {},
  localCamera: null,
  localScreen: null,
  userVolumes: loadVolumes(),
}));

function loadVolumes(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem('spawnwire:user-volumes') ?? '{}') as Record<string, number>;
  } catch {
    return {};
  }
}

interface Peer {
  userId: string;
  /** Their connection to the server (socket) this call connection is with; null until known. */
  session: string | null;
  pc: RTCPeerConnection;
  polite: boolean;
  makingOffer: boolean;
  /** Our offer while it is being sent (see handleSignal). */
  offering: Promise<void> | null;
  /** Polite side, until the first offer arrives: the other side makes it (see createPeer). */
  awaitingOffer: boolean;
  offerTimer: ReturnType<typeof setTimeout> | null;
  ignoreOffer: boolean;
  settingRemoteAnswer: boolean;
  queue: Promise<void>;
  audio: HTMLAudioElement;
  streams: Map<string, MediaStream>;
  kinds: Record<string, StreamKind>;
  senders: { audio?: RTCRtpSender; camera?: RTCRtpSender; screen?: RTCRtpSender };
  disconnectedTimer: ReturnType<typeof setTimeout> | null;
  /** The current ICE generation produced a relay candidate. */
  gotRelay: boolean;
  /** Failed connection attempts since the last success. */
  failures: number;
  retryTimer: ReturnType<typeof setTimeout> | null;
  relayFailed: boolean;
}

const peers = new Map<string, Peer>();
let iceCache: { servers: RTCIceServer[]; refreshAt: number } | null = null;
let credentialTimer: ReturnType<typeof setTimeout> | null = null;
let relayToastShown = false;

let rawMic: MediaStream | null = null;
let micStream: MediaStream | null = null;
let micGain: GainNode | null = null;
let cameraStream: MediaStream | null = null;
let screenStream: MediaStream | null = null;
let audioCtx: AudioContext | null = null;
let meterTimer: ReturnType<typeof setInterval> | null = null;
const meters = new Map<string, { analyser: AnalyserNode; source: MediaStreamAudioSourceNode; lastLoud: number }>();
let mutedBeforeDeafen = false;
let audioHost: HTMLDivElement | null = null;

const setState = useVoice.setState;
const getState = useVoice.getState;

function myId(): string | null {
  return useStore.getState().me?.id ?? null;
}

function settings() {
  return useStore.getState().me?.settings.audio;
}

function ensureAudioHost(): HTMLDivElement {
  if (!audioHost) {
    audioHost = document.createElement('div');
    audioHost.hidden = true;
    audioHost.id = 'spawnwire-remote-audio';
    document.body.appendChild(audioHost);
  }
  return audioHost;
}

function ctx(): AudioContext {
  audioCtx ??= new AudioContext();
  if (audioCtx.state === 'suspended') void audioCtx.resume();
  return audioCtx;
}

/** The relay (TURN) servers with current temporary credentials. Throws when the server offers no relay. */
async function iceServers(): Promise<RTCIceServer[]> {
  if (iceCache && iceCache.refreshAt > Date.now()) return iceCache.servers;
  const res = await http.get<{ iceServers: IceServerConfig[]; ttlSeconds: number | null }>('/voice/ice-servers');
  const servers = relayServers(res.iceServers) as RTCIceServer[];
  if (servers.length === 0) throw new Error(RELAY_NOT_CONFIGURED);
  // Refresh TURN credentials well before they expire.
  iceCache = { servers, refreshAt: Date.now() + (res.ttlSeconds ? (res.ttlSeconds * 1000) / 2 : 10 * 60_000) };
  return servers;
}

/** Every call connection is built from this: relay candidates only, so media never goes direct. */
export function rtcConfig(servers: RTCIceServer[]): RTCConfiguration {
  return { iceServers: servers, iceTransportPolicy: 'relay' };
}

/** Resolves once the relay hands out an address; throws when it cannot be reached in time. */
async function probeRelay(servers: RTCIceServer[]): Promise<void> {
  const pc = new RTCPeerConnection(rtcConfig(servers));
  try {
    const reached = new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => resolve(false), RELAY_TIMEOUT_MS);
      pc.onicecandidate = ({ candidate }) => {
        if (candidate && candidateType(candidate.candidate) !== 'relay') return;
        clearTimeout(timer);
        // A relay candidate, or gathering finished without one.
        resolve(!!candidate);
      };
    });
    pc.createDataChannel('probe');
    await pc.setLocalDescription();
    if (!(await reached)) throw new Error(RELAY_UNREACHABLE);
  } finally {
    pc.close();
  }
}

/** Gives open connections fresh TURN credentials before the old ones expire, for later ICE restarts. */
function scheduleCredentialRefresh(): void {
  if (credentialTimer) clearTimeout(credentialTimer);
  credentialTimer = null;
  if (!getState().channelId || !iceCache) return;
  credentialTimer = setTimeout(() => {
    credentialTimer = null;
    void iceServers()
      .then((servers) => {
        for (const p of peers.values()) p.pc.setConfiguration({ ...p.pc.getConfiguration(), ...rtcConfig(servers) });
      })
      .catch((err) => console.warn('TURN credential refresh failed', err))
      .finally(scheduleCredentialRefresh);
  }, Math.max(10_000, iceCache.refreshAt - Date.now()));
}

const debug = (...args: unknown[]) => {
  if (import.meta.env.DEV) console.debug('[voice]', ...args);
};

function signal(to: string, payload: SignalPayload): void {
  // Only relay candidates and no local addresses leave this browser (the server filters the same way).
  const clean = sanitizeSignal(payload);
  if (!clean) return;
  debug('send', clean.kind, clean.kind === 'description' ? clean.description.type : '', 'to', to);
  getSocket()?.emit('voice:signal', { to, signal: clean });
}

function emitUpdate(patch: { selfMute?: boolean; selfDeaf?: boolean; video?: boolean; screen?: boolean }): void {
  getSocket()?.emit('voice:update', patch, (res) => {
    if (res && !res.ok) ui.toast(res.error, 'error');
  });
}

// ---- Local media -------------------------------------------------------------------

/** Real microphones, without the browser's "default" and "communications" aliases. */
export async function listMicrophones(): Promise<MediaDeviceInfo[]> {
  const all = await navigator.mediaDevices.enumerateDevices();
  return all.filter((d) => d.kind === 'audioinput' && d.deviceId && d.deviceId !== 'default' && d.deviceId !== 'communications');
}

/**
 * The microphone you picked, if it is connected: by the id saved on your account, else by its name (ids differ
 * between browsers). Undefined means "any": the browser's default microphone.
 */
export async function preferredMicrophone(): Promise<string | undefined> {
  const saved = settings()?.inputDeviceId ?? undefined;
  const label = micLabel.get();
  if (!saved && !label) return undefined;
  try {
    const inputs = await listMicrophones();
    if (saved && inputs.some((d) => d.deviceId === saved)) return saved;
    const byName = label ? inputs.find((d) => d.label === label) : undefined;
    if (byName) return byName.deviceId;
    // Before microphone permission the browser hides devices, so trust the saved id.
    if (inputs.every((d) => !d.label)) return saved;
  } catch {
    /* device list unavailable */
  }
  return undefined;
}

/**
 * Opens a particular microphone or camera, or any one if it is missing. The device has to be `exact`:
 * Chromium treats an `ideal` device as a hint and opens the default instead.
 */
export async function openDevice(kind: 'audio' | 'video', deviceId: string | null | undefined, constraints: MediaTrackConstraints = {}): Promise<MediaStream> {
  if (deviceId) {
    try {
      return await navigator.mediaDevices.getUserMedia({ [kind]: { ...constraints, deviceId: { exact: deviceId } } });
    } catch (err) {
      // Unplugged, renamed or busy: fall through to any device. A refused permission is final.
      if ((err as Error).name === 'NotAllowedError' || (err as Error).name === 'SecurityError') throw err;
    }
  }
  return navigator.mediaDevices.getUserMedia({ [kind]: Object.keys(constraints).length ? constraints : true });
}

async function acquireMic(): Promise<MediaStream | null> {
  const s = settings();
  const deviceId = await preferredMicrophone();
  try {
    const stream = await openDevice('audio', deviceId, {
      echoCancellation: s?.echoCancellation ?? true,
      noiseSuppression: s?.noiseSuppression ?? true,
      autoGainControl: s?.autoGainControl ?? true,
    });
    rawMic = stream;
    // Unplugged mid-call: carry on with another microphone.
    stream.getAudioTracks()[0]?.addEventListener('ended', () => {
      if (rawMic === stream) void followPreferredMic();
    });
    // Route through a gain node so the input volume setting applies to what peers hear.
    const ac = ctx();
    const source = ac.createMediaStreamSource(stream);
    micGain = ac.createGain();
    micGain.gain.value = (s?.inputVolume ?? 100) / 100;
    const dest = ac.createMediaStreamDestination();
    source.connect(micGain).connect(dest);
    micStream = dest.stream;
    addMeter('local', stream);
    return micStream;
  } catch (err) {
    console.warn('microphone unavailable', err);
    return null;
  }
}

function micTrack(): MediaStreamTrack | null {
  return micStream?.getAudioTracks()[0] ?? null;
}

function applyMuteToTrack(): void {
  const track = micTrack();
  if (!track) return;
  const me = myId();
  const vs = me ? findMyVoiceState() : null;
  // The stage audience (suppress) can listen but not talk, like a server mute.
  track.enabled = !getState().muted && !getState().deafened && !vs?.serverMute && !vs?.suppress;
}

/** Stage roles: raise or lower your hand, step up or down, or (moderators) choose who speaks. */
export function stageUpdate(payload: { userId?: string; suppress?: boolean; requestToSpeak?: boolean }, channelId = getState().channelId): Promise<void> {
  return new Promise((resolve, reject) => {
    const socket = getSocket();
    if (!socket || !channelId) return reject(new Error('You are not on a stage.'));
    socket.emit('stage:update', { channelId, ...payload }, (res) => (res.ok ? resolve() : reject(new Error(res.error))));
  });
}

function findMyVoiceState(): VoiceState | null {
  const { channelId } = getState();
  const me = myId();
  if (!channelId || !me) return null;
  return useStore.getState().voiceStates[channelId]?.[me] ?? null;
}

// ---- Speaking detection ---------------------------------------------------------

function addMeter(key: string, stream: MediaStream): void {
  removeMeter(key);
  if (stream.getAudioTracks().length === 0) return;
  try {
    const ac = ctx();
    const source = ac.createMediaStreamSource(stream);
    const analyser = ac.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    meters.set(key, { analyser, source, lastLoud: 0 });
    startMeterLoop();
  } catch {
    /* stream without audio */
  }
}

function removeMeter(key: string): void {
  const m = meters.get(key);
  if (m) {
    m.source.disconnect();
    meters.delete(key);
  }
}

export function levelOf(analyser: AnalyserNode): number {
  const data = new Uint8Array(analyser.fftSize);
  analyser.getByteTimeDomainData(data);
  let sum = 0;
  for (const v of data) {
    const x = (v - 128) / 128;
    sum += x * x;
  }
  // RMS mapped to a 0-100 scale that roughly matches perceived loudness.
  return Math.min(100, Math.round(Math.sqrt(sum / data.length) * 300));
}

function startMeterLoop(): void {
  if (meterTimer) return;
  meterTimer = setInterval(() => {
    const now = Date.now();
    const me = myId();
    const threshold = settings()?.speakingThreshold ?? 12;
    const next: Record<string, boolean> = {};
    for (const [key, m] of meters) {
      const level = levelOf(m.analyser);
      const userId = key === 'local' ? me : key;
      if (!userId) continue;
      const loud = key === 'local' ? level > threshold && !getState().muted && !getState().deafened : level > 6;
      if (loud) m.lastLoud = now;
      next[userId] = now - m.lastLoud < 350;
    }
    const prev = getState().speaking;
    const changed = Object.keys(next).length !== Object.keys(prev).length || Object.entries(next).some(([k, v]) => prev[k] !== v);
    if (changed) setState({ speaking: next });
  }, 100);
}

// ---- Peer connections --------------------------------------------------------------

function publishMeta(peer: Peer): void {
  const streams: Record<string, StreamKind> = {};
  if (micStream) streams[micStream.id] = 'audio';
  if (cameraStream) streams[cameraStream.id] = 'camera';
  if (screenStream) streams[screenStream.id] = 'screen';
  signal(peer.userId, { kind: 'meta', streams });
}

function refreshRemote(peer: Peer): void {
  let camera: MediaStream | null = null;
  let screen: MediaStream | null = null;
  for (const [id, stream] of peer.streams) {
    const live = stream.getVideoTracks().some((t) => t.readyState === 'live' && !t.muted);
    if (!live) continue;
    const kind = peer.kinds[id];
    if (kind === 'screen') screen = stream;
    else if (kind === 'camera' || !camera) camera = stream;
  }
  setState((s) => ({ remote: { ...s.remote, [peer.userId]: { camera, screen, connection: peer.pc.connectionState, relayFailed: peer.relayFailed } } }));
}

function applyOutput(peer: Peer): void {
  const s = settings();
  const st = getState();
  const userVol = st.userVolumes[peer.userId] ?? 100;
  peer.audio.muted = st.deafened;
  peer.audio.volume = Math.min(1, ((s?.outputVolume ?? 100) / 100) * (userVol / 100));
  const sinkId = s?.outputDeviceId;
  const el = peer.audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
  if (sinkId && el.setSinkId) void el.setSinkId(sinkId).catch(() => {});
}

function createPeer(userId: string, servers: RTCIceServer[], session: string | null): Peer {
  const me = myId() ?? '';
  const pc = new RTCPeerConnection(rtcConfig(servers));
  const audio = document.createElement('audio');
  audio.autoplay = true;
  ensureAudioHost().appendChild(audio);
  // Deterministic roles: the peer with the larger id yields during offer collisions.
  const polite = me > userId;
  const peer: Peer = {
    userId,
    session,
    pc,
    polite,
    makingOffer: false,
    offering: null,
    // Only the impolite side makes the first offer, so the first negotiation never collides, even when both join at
    // once. A collided first negotiation can leave the connection unable to gather candidates (see handleSignal).
    awaitingOffer: polite,
    offerTimer: null,
    ignoreOffer: false,
    settingRemoteAnswer: false,
    queue: Promise.resolve(),
    audio,
    streams: new Map(),
    kinds: {},
    senders: {},
    disconnectedTimer: null,
    gotRelay: false,
    failures: 0,
    retryTimer: null,
    relayFailed: false,
  };
  peers.set(userId, peer);
  applyOutput(peer);

  // A pre-negotiated data channel keeps ICE and DTLS up even when nobody sends
  // media; two listen-only peers would otherwise negotiate only inactive tracks.
  pc.createDataChannel('spawnwire', { negotiated: true, id: 0 });

  const track = micTrack();
  if (track && micStream) peer.senders.audio = pc.addTrack(track, micStream);
  else pc.addTransceiver('audio', { direction: 'recvonly' });
  if (cameraStream) peer.senders.camera = pc.addTrack(cameraStream.getVideoTracks()[0]!, cameraStream);
  if (screenStream) peer.senders.screen = pc.addTrack(screenStream.getVideoTracks()[0]!, screenStream);

  pc.onnegotiationneeded = () => {
    // Not while we wait for their first offer: after we answer it, this fires again if anything is still left out.
    if (!peer.awaitingOffer) void makeOffer(peer);
  };

  pc.onicecandidate = ({ candidate }) => {
    if (candidate && candidateType(candidate.candidate) === 'relay') peer.gotRelay = true;
    signal(userId, { kind: 'candidate', candidate: candidate ? candidate.toJSON() : null });
  };

  // The relay gave us no address this round (unreachable, or credentials refused): retry, never go direct.
  pc.onicegatheringstatechange = () => {
    if (pc.iceGatheringState === 'complete' && !peer.gotRelay) retryPeer(peer);
  };

  pc.ontrack = ({ track: remoteTrack, streams }) => {
    const stream = streams[0] ?? new MediaStream([remoteTrack]);
    peer.streams.set(stream.id, stream);
    if (remoteTrack.kind === 'audio') {
      peer.audio.srcObject = stream;
      void peer.audio.play().catch(() => {});
      addMeter(userId, stream);
    }
    remoteTrack.onmute = () => refreshRemote(peer);
    remoteTrack.onunmute = () => refreshRemote(peer);
    remoteTrack.onended = () => refreshRemote(peer);
    stream.onremovetrack = () => refreshRemote(peer);
    refreshRemote(peer);
  };

  pc.onconnectionstatechange = () => {
    if (pc.connectionState === 'connected') {
      peer.failures = 0;
      peer.relayFailed = false;
      if (peer.retryTimer) clearTimeout(peer.retryTimer);
      peer.retryTimer = null;
    }
    refreshRemote(peer);
    if (pc.connectionState === 'failed') retryPeer(peer);
    if (pc.connectionState === 'disconnected') {
      peer.disconnectedTimer ??= setTimeout(() => {
        peer.disconnectedTimer = null;
        if (pc.connectionState === 'disconnected') void restartPeer(peer);
      }, 4000);
    } else if (peer.disconnectedTimer) {
      clearTimeout(peer.disconnectedTimer);
      peer.disconnectedTimer = null;
    }
  };

  publishMeta(peer);
  if (peer.awaitingOffer) awaitFirstOffer(peer, 1);
  return peer;
}

/**
 * The other side sets up its end of the connection, and makes the first offer, when it first hears from us. Until
 * that offer arrives, remind them every few seconds that we are here (they may have missed it), then offer ourselves.
 */
function awaitFirstOffer(peer: Peer, reminder: number): void {
  peer.offerTimer = setTimeout(() => {
    peer.offerTimer = null;
    if (!peer.awaitingOffer || peers.get(peer.userId) !== peer) return;
    if (reminder <= FIRST_OFFER_REMINDERS) {
      publishMeta(peer);
      awaitFirstOffer(peer, reminder + 1);
    } else {
      peer.awaitingOffer = false;
      void makeOffer(peer);
    }
  }, FIRST_OFFER_REMINDER_MS);
}

function makeOffer(peer: Peer): Promise<void> {
  const { pc } = peer;
  peer.makingOffer = true;
  peer.offering = (async () => {
    try {
      await pc.setLocalDescription();
      if (pc.localDescription) signal(peer.userId, { kind: 'description', description: pc.localDescription.toJSON() });
    } catch (err) {
      console.warn('negotiation failed', err);
    } finally {
      peer.makingOffer = false;
      peer.offering = null;
    }
  })();
  return peer.offering;
}

/** ICE restart through the relay, with fresh TURN credentials when the old ones are due for renewal. */
async function restartPeer(peer: Peer): Promise<void> {
  try {
    const servers = await iceServers();
    if (peers.get(peer.userId) !== peer) return;
    peer.pc.setConfiguration({ ...peer.pc.getConfiguration(), ...rtcConfig(servers) });
  } catch (err) {
    console.warn('could not renew TURN credentials', err);
  }
  if (peers.get(peer.userId) !== peer) return;
  peer.gotRelay = false;
  peer.pc.restartIce();
}

/** Retries a connection that could not get through the relay: quickly at first, then every 30 seconds. */
function retryPeer(peer: Peer): void {
  if (peer.retryTimer || peers.get(peer.userId) !== peer) return;
  peer.failures++;
  if (peer.failures > QUICK_RETRIES && !peer.relayFailed) {
    peer.relayFailed = true;
    refreshRemote(peer);
    if (!relayToastShown) {
      relayToastShown = true;
      ui.toast("Can't connect to someone in this call through the voice relay. Calls never connect directly, to keep IP addresses private. Still trying.", 'error');
    }
  }
  peer.retryTimer = setTimeout(
    () => {
      peer.retryTimer = null;
      if (peers.get(peer.userId) === peer && peer.pc.connectionState !== 'connected') void restartPeer(peer);
    },
    peer.failures <= QUICK_RETRIES ? 1000 * peer.failures : 30_000,
  );
}

function closePeer(userId: string): void {
  const peer = peers.get(userId);
  if (!peer) return;
  peers.delete(userId);
  if (peer.disconnectedTimer) clearTimeout(peer.disconnectedTimer);
  if (peer.retryTimer) clearTimeout(peer.retryTimer);
  if (peer.offerTimer) clearTimeout(peer.offerTimer);
  peer.pc.ontrack = null;
  peer.pc.onicecandidate = null;
  peer.pc.onicegatheringstatechange = null;
  peer.pc.onnegotiationneeded = null;
  peer.pc.onconnectionstatechange = null;
  peer.pc.close();
  peer.audio.srcObject = null;
  peer.audio.remove();
  removeMeter(userId);
  setState((s) => {
    const remote = { ...s.remote };
    delete remote[userId];
    const speaking = { ...s.speaking };
    delete speaking[userId];
    return { remote, speaking };
  });
}

/**
 * Works around a Chromium bug. When the first negotiation with someone was itself a collision (both joined or
 * reconnected at once), every later collision in which we roll back our offer gives our audio a new media
 * channel but keeps its SSRC, so the sender never attaches the microphone to it: the track stays live and the
 * transceiver sendrecv, yet nothing is sent and they stop hearing us. Setting the track again, once our answer
 * has created that channel, attaches it. Cameras and screen shares are not affected.
 */
async function reattachMic(peer: Peer): Promise<void> {
  const track = micTrack();
  if (!peer.senders.audio?.track || !track) return;
  await peer.senders.audio.replaceTrack(track).catch((err) => console.warn('could not re-attach the microphone', err));
}

/**
 * Notes which of their sessions our connection with someone belongs to. When they show up from another session (they
 * crashed or lost their connection and rejoined before the server noticed, so it never announced that they left), the
 * old connection talks to a page that is gone: drop it, and a new one is set up as for anyone joining.
 */
function checkSession(userId: string, session: string | null | undefined): void {
  const peer = peers.get(userId);
  if (!peer || !session) return;
  if (!peer.session) peer.session = session;
  else if (peer.session !== session) closePeer(userId);
}

async function handleSignal(from: string, received: SignalPayload, fromSession?: string): Promise<void> {
  const { channelId } = getState();
  // Never use a peer's non-relay address, even if a modified client or older server sent one.
  const payload = sanitizeSignal(received);
  if (!payload) return;
  debug('recv', payload.kind, payload.kind === 'description' ? payload.description.type : '', 'from', from, 'in call:', !!channelId);
  if (!channelId) return;
  checkSession(from, fromSession);
  let peer = peers.get(from);
  if (!peer) {
    const inCall = useStore.getState().voiceStates[channelId]?.[from];
    if (!inCall && payload.kind !== 'description') return;
    let servers: RTCIceServer[];
    try {
      servers = await iceServers();
    } catch (err) {
      console.warn('no relay for a new peer', err);
      return;
    }
    if (getState().channelId !== channelId) return;
    // Another signal from the same person may have created it while we waited.
    peer = peers.get(from) ?? createPeer(from, servers, fromSession ?? null);
  }
  const p = peer;
  // Process each peer's signals strictly in order (candidates must follow descriptions).
  p.queue = p.queue.then(async () => {
    const pc = p.pc;
    try {
      if (payload.kind === 'description') {
        const description = payload.description as RTCSessionDescriptionInit;
        const readyForOffer = !p.makingOffer && (pc.signalingState === 'stable' || p.settingRemoteAnswer);
        const collision = description.type === 'offer' && !readyForOffer;
        p.ignoreOffer = !p.polite && collision;
        if (p.ignoreOffer) return;
        // Let our own offer finish before theirs rolls it back. Rolling back an offer that is still being set can
        // leave Chromium unable to gather ICE candidates on this connection for good (even after an ICE restart).
        if (collision) await p.offering;
        p.settingRemoteAnswer = description.type === 'answer';
        if (description.type === 'offer') p.awaitingOffer = false;
        await pc.setRemoteDescription(description);
        p.settingRemoteAnswer = false;
        if (description.type === 'offer') {
          await pc.setLocalDescription();
          if (pc.localDescription) signal(from, { kind: 'description', description: pc.localDescription.toJSON() });
          // We yielded: the remote offer rolled our own back.
          if (collision) await reattachMic(p);
        }
      } else if (payload.kind === 'candidate') {
        try {
          await pc.addIceCandidate(payload.candidate ?? undefined);
        } catch (err) {
          if (!p.ignoreOffer) console.warn('ICE candidate rejected', err);
        }
      } else if (payload.kind === 'meta') {
        p.kinds = payload.streams;
        refreshRemote(p);
      }
    } catch (err) {
      console.warn('signal handling failed', err);
    }
  });
  await p.queue;
}

// ---- Public API ------------------------------------------------------------------------

async function connectToRoom(channelId: string): Promise<boolean> {
  const socket = getSocket();
  if (!socket) throw new Error('Not connected to the server.');
  const servers = await iceServers();
  // Fail before joining, rather than sit in a call that can never connect.
  await probeRelay(servers);
  if (getState().channelId !== channelId) return false;
  const { muted, deafened } = getState();
  const res = await new Promise<{ ok: true; data?: { states: VoiceState[] } } | { ok: false; error: string }>((resolve) =>
    socket.timeout(10_000).emit('voice:join', { channelId, selfMute: muted, selfDeaf: deafened }, (err: Error | null, r: unknown) =>
      resolve(err ? { ok: false, error: 'The server did not respond.' } : (r as { ok: true; data?: { states: VoiceState[] } })),
    ),
  );
  if (!res.ok) {
    setState({ status: 'error', error: res.error });
    ui.toast(res.error, 'error');
    return false;
  }
  const states = res.data?.states ?? [];
  actions.setVoiceChannelStates(channelId, states);
  const me = myId();
  for (const state of states) {
    checkSession(state.userId, state.sessionId);
    if (state.userId !== me && !peers.has(state.userId)) createPeer(state.userId, servers, state.sessionId);
  }
  applyMuteToTrack();
  scheduleCredentialRefresh();
  return true;
}

export async function joinVoice(channelId: string): Promise<void> {
  if (typeof RTCPeerConnection === 'undefined' || !navigator.mediaDevices) {
    ui.toast('This browser does not support voice calls.', 'error');
    return;
  }
  if (getState().channelId === channelId) return;
  if (getState().channelId) await leaveVoice();
  setState({ channelId, status: 'connecting', error: null, video: false, screen: false });
  const mic = await acquireMic();
  setState({ hasMic: !!mic, muted: mic ? getState().muted : true });
  if (!mic) ui.toast('No microphone available. You joined listen-only.');
  try {
    const ok = await connectToRoom(channelId);
    // Left or switched channels while connecting: that call is no longer ours to clean up.
    if (getState().channelId !== channelId) return;
    if (!ok) {
      cleanup();
      return;
    }
    setState({ status: 'connected' });
    if (useStore.getState().me?.settings.notificationSounds) playChime('join');
  } catch (err) {
    if (getState().channelId !== channelId) return;
    cleanup();
    setState({ status: 'error', error: (err as Error).message });
    ui.toast((err as Error).message, 'error');
  }
}

function cleanup(): void {
  for (const id of [...peers.keys()]) closePeer(id);
  for (const s of [rawMic, micStream, cameraStream, screenStream]) s?.getTracks().forEach((t) => t.stop());
  rawMic = micStream = cameraStream = screenStream = null;
  micGain = null;
  for (const key of [...meters.keys()]) removeMeter(key);
  if (meterTimer) clearInterval(meterTimer);
  meterTimer = null;
  if (credentialTimer) clearTimeout(credentialTimer);
  credentialTimer = null;
  relayToastShown = false;
  setState({ channelId: null, status: 'idle', video: false, screen: false, speaking: {}, remote: {}, localCamera: null, localScreen: null });
}

export async function leaveVoice(): Promise<void> {
  const { channelId } = getState();
  if (!channelId) return;
  getSocket()?.emit('voice:leave');
  const me = myId();
  if (me) actions.setVoiceState(channelId, me, null);
  cleanup();
  if (useStore.getState().me?.settings.notificationSounds) playChime('leave');
}

export function toggleMute(): void {
  const st = getState();
  if (st.channelId && !st.hasMic) {
    ui.toast('No microphone is available.');
    return;
  }
  if (st.deafened) {
    // Unmuting while deafened also undeafens, like a hardware headset.
    setState({ deafened: false, muted: false });
    for (const p of peers.values()) applyOutput(p);
  } else {
    setState({ muted: !st.muted });
  }
  applyMuteToTrack();
  if (st.channelId) emitUpdate({ selfMute: getState().muted, selfDeaf: getState().deafened });
}

export function toggleDeafen(): void {
  const st = getState();
  const deafened = !st.deafened;
  if (deafened) mutedBeforeDeafen = st.muted;
  setState({ deafened, muted: deafened ? true : mutedBeforeDeafen });
  for (const p of peers.values()) applyOutput(p);
  applyMuteToTrack();
  if (st.channelId) emitUpdate({ selfMute: getState().muted, selfDeaf: deafened });
}

export async function toggleCamera(): Promise<void> {
  const st = getState();
  if (!st.channelId) return;
  if (st.video) {
    for (const p of peers.values()) {
      if (p.senders.camera) p.pc.removeTrack(p.senders.camera);
      p.senders.camera = undefined;
    }
    cameraStream?.getTracks().forEach((t) => t.stop());
    cameraStream = null;
    setState({ video: false, localCamera: null });
    emitUpdate({ video: false });
    return;
  }
  try {
    const deviceId = settings()?.videoDeviceId;
    cameraStream = await openDevice('video', deviceId, { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } });
  } catch {
    ui.toast('Could not start your camera. Check that it is connected and allowed.', 'error');
    return;
  }
  const track = cameraStream.getVideoTracks()[0]!;
  track.onended = () => {
    if (getState().video) void toggleCamera();
  };
  for (const p of peers.values()) {
    p.senders.camera = p.pc.addTrack(track, cameraStream);
    publishMeta(p);
  }
  setState({ video: true, localCamera: cameraStream });
  emitUpdate({ video: true });
}

export async function toggleScreenShare(): Promise<void> {
  const st = getState();
  if (!st.channelId) return;
  if (st.screen) {
    stopScreen();
    return;
  }
  if (!navigator.mediaDevices.getDisplayMedia) {
    ui.toast('Screen sharing is not supported in this browser.', 'error');
    return;
  }
  try {
    screenStream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: { ideal: 30 } }, audio: false });
  } catch {
    return; // the user cancelled the picker
  }
  const track = screenStream.getVideoTracks()[0]!;
  track.contentHint = 'detail';
  // The browser's own "Stop sharing" button ends the track.
  track.onended = () => stopScreen();
  for (const p of peers.values()) {
    p.senders.screen = p.pc.addTrack(track, screenStream);
    publishMeta(p);
  }
  setState({ screen: true, localScreen: screenStream });
  emitUpdate({ screen: true });
}

function stopScreen(): void {
  if (!screenStream) return;
  for (const p of peers.values()) {
    if (p.senders.screen) p.pc.removeTrack(p.senders.screen);
    p.senders.screen = undefined;
  }
  screenStream.getTracks().forEach((t) => t.stop());
  screenStream = null;
  setState({ screen: false, localScreen: null });
  emitUpdate({ screen: false });
}

let micSwap: Promise<void> | null = null;

/** Swaps the microphone in place (after a device change) without renegotiating. */
export function restartMicrophone(): Promise<void> {
  // One swap at a time: unplugging fires both 'ended' and 'devicechange'.
  micSwap ??= swapMicrophone().finally(() => (micSwap = null));
  return micSwap;
}

/**
 * In a call, keeps you on the microphone you picked: switches to another one when yours goes away and back
 * when it is plugged in again.
 */
async function followPreferredMic(): Promise<void> {
  if (!getState().channelId) return;
  const current = rawMic?.getAudioTracks()[0];
  const want = await preferredMicrophone();
  const live = current?.readyState === 'live';
  if (!live || (want && current?.getSettings().deviceId !== want)) await restartMicrophone();
}

async function swapMicrophone(): Promise<void> {
  if (!getState().channelId) return;
  const oldRaw = rawMic;
  const oldStream = micStream;
  const stream = await acquireMic();
  if (!stream) return;
  const track = stream.getAudioTracks()[0]!;
  for (const p of peers.values()) {
    if (p.senders.audio) await p.senders.audio.replaceTrack(track);
    else p.senders.audio = p.pc.addTrack(track, stream);
  }
  oldRaw?.getTracks().forEach((t) => t.stop());
  oldStream?.getTracks().forEach((t) => t.stop());
  setState({ hasMic: true });
  applyMuteToTrack();
}

export function setInputVolume(volume: number): void {
  if (micGain) micGain.gain.value = volume / 100;
}

export function refreshOutputs(): void {
  for (const p of peers.values()) applyOutput(p);
}

export function setUserVolume(userId: string, volume: number): void {
  setState((s) => ({ userVolumes: { ...s.userVolumes, [userId]: volume } }));
  try {
    localStorage.setItem('spawnwire:user-volumes', JSON.stringify(getState().userVolumes));
  } catch {
    /* ignore */
  }
  const p = peers.get(userId);
  if (p) applyOutput(p);
}

/** Microphone level meter for the settings page, on the microphone a call would use. Returns a stop function. */
export async function startMicTest(onLevel: (level: number) => void): Promise<() => void> {
  const stream = await openDevice('audio', await preferredMicrophone());
  const ac = ctx();
  const source = ac.createMediaStreamSource(stream);
  const analyser = ac.createAnalyser();
  analyser.fftSize = 512;
  source.connect(analyser);
  const timer = setInterval(() => onLevel(levelOf(analyser)), 80);
  return () => {
    clearInterval(timer);
    source.disconnect();
    stream.getTracks().forEach((t) => t.stop());
  };
}

/** Development diagnostics: the state of every peer connection. */
export function debugPeers(): Record<string, Record<string, unknown>> {
  const lines = (sdp: string | undefined, prefix: string) => (sdp ?? '').split('\r\n').filter((l) => l.startsWith(prefix));
  return Object.fromEntries(
    [...peers].map(([id, p]) => [
      id,
      {
        signaling: p.pc.signalingState,
        gathering: p.pc.iceGatheringState,
        ice: p.pc.iceConnectionState,
        connection: p.pc.connectionState,
        localMedia: lines(p.pc.localDescription?.sdp, 'm='),
        remoteMedia: lines(p.pc.remoteDescription?.sdp, 'm='),
        localCandidates: lines(p.pc.localDescription?.sdp, 'a=candidate').length,
        remoteCandidates: lines(p.pc.remoteDescription?.sdp, 'a=candidate').length,
      },
    ]),
  );
}

/** Diagnostics: the candidate pair each connection is using. With relay-only calls both ends are "relay". */
export async function relayReport(): Promise<Record<string, { connection: string; local: string | null; remote: string | null; protocol: string | null; relayProtocol: string | null }>> {
  const out: Awaited<ReturnType<typeof relayReport>> = {};
  for (const [id, p] of peers) {
    const stats = await p.pc.getStats();
    let pair: Record<string, unknown> | undefined;
    stats.forEach((s: Record<string, unknown>) => {
      if (s.type === 'transport' && typeof s.selectedCandidatePairId === 'string') pair = stats.get(s.selectedCandidatePairId);
    });
    // Firefox marks the pair in use instead of naming it on the transport.
    stats.forEach((s: Record<string, unknown>) => {
      if (!pair && s.type === 'candidate-pair' && (s.selected === true || (s.nominated === true && s.state === 'succeeded'))) pair = s;
    });
    const local = pair ? stats.get(pair.localCandidateId as string) : undefined;
    const remote = pair ? stats.get(pair.remoteCandidateId as string) : undefined;
    out[id] = {
      connection: p.pc.connectionState,
      local: local?.candidateType ?? null,
      remote: remote?.candidateType ?? null,
      protocol: local?.protocol ?? null,
      relayProtocol: local?.relayProtocol ?? null,
    };
  }
  return out;
}

// ---- Wiring to the gateway -----------------------------------------------------------

let wired = false;
export function wireVoice(): void {
  if (wired) return;
  wired = true;
  navigator.mediaDevices?.addEventListener('devicechange', () => void followPreferredMic());
  const attach = () => {
    const socket = getSocket();
    if (!socket || (socket as unknown as { __voiceWired?: boolean }).__voiceWired) return;
    (socket as unknown as { __voiceWired?: boolean }).__voiceWired = true;
    socket.on('voice:signal', ({ from, fromSession, signal: payload }) => void handleSignal(from, payload, fromSession));
    socket.on('voice:state', ({ channelId, userId, state }) => {
      const mine = getState().channelId;
      if (channelId !== mine || userId === myId()) {
        if (userId === myId() && state) {
          applyMuteToTrack();
          // Moved to a stage's audience: cameras and screens go off.
          if (state.suppress) {
            if (getState().video) void toggleCamera();
            if (getState().screen) stopScreen();
          }
        }
        return;
      }
      if (!state) {
        closePeer(userId);
        if (useStore.getState().me?.settings.notificationSounds) playChime('leave');
      } else {
        checkSession(userId, state.sessionId);
      }
    });
    socket.on('voice:force_leave', ({ reason }) => {
      cleanup();
      ui.toast(reason, 'error');
    });
  };
  attach();
  // After a reconnect the server has dropped us from the call: rejoin and rebuild peers.
  onReady(() => {
    attach();
    const { channelId } = getState();
    if (!channelId) return;
    for (const id of [...peers.keys()]) closePeer(id);
    void connectToRoom(channelId)
      .then((ok) => {
        if (getState().channelId !== channelId) return;
        if (!ok) cleanup();
        else emitUpdate({ video: getState().video, screen: getState().screen });
      })
      .catch((err: Error) => {
        // The relay is unreachable after a reconnect: leave rather than connect directly.
        if (getState().channelId !== channelId) return;
        getSocket()?.emit('voice:leave');
        cleanup();
        setState({ status: 'error', error: err.message });
        ui.toast(err.message, 'error');
      });
  });
  navigator.mediaDevices?.addEventListener?.('devicechange', () => {
    const track = rawMic?.getAudioTracks()[0];
    if (getState().channelId && track && track.readyState === 'ended') void restartMicrophone();
  });
  window.addEventListener('beforeunload', () => {
    if (getState().channelId) getSocket()?.emit('voice:leave');
  });
}
